import React from "react";
import { Buttons } from "src/components/Buttons/Buttons.component";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { DA_Accordion } from "@danishagro/shared/src/components/molecules/Accordion/Accordion.component";
import { DA_Container } from "@danishagro/shared/src/components/atoms/Container/Container.component";
import { DA_Text } from "@danishagro/shared/src/components/atoms/Text/Text.component";
import {
    DA_Title,
    DA_TitleTag,
} from "@danishagro/shared/src/components/atoms/Title/Title.component";
import { Helmet } from "react-helmet";
import { ImageViewer } from "src/components/ImageViewer/ImageViewer.component";
import { useBackgroundColor } from "@danishagro/cms/src/blocks/hooks/useBackgroundColor/useBackgroundColor.hook";
import { usePadding } from "@danishagro/cms/src/blocks/hooks/usePadding/usePadding.hook";
import { usePosition } from "./hooks/usePosition.hook";
import { TextAccordionsProps } from "./TextAccordions.interface";
import S from "./TextAccordions.module.scss";

const TextAccordions = (props: TextAccordionsProps) => {
    const { positionClassName } = usePosition(props.position, S);
    const { backgroundColorClass, alternativeButtonTheme, hasBackgroundColor } = useBackgroundColor(
        props.backgroundColor
    );
    const { paddingClass } = usePadding(props.isCompact, hasBackgroundColor);

    // Ensure fAQContent is strictly true (not null, undefined, or false)
    const isFaqContent = props.fAQContent === true;

    // Function to generate the FAQ schema for each accordion item
    const generateFAQSchema = () => {
        return {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: props.items?.map((item) => ({
                "@type": "Question",
                name: item.headline, // Title from the CMS item
                acceptedAnswer: {
                    "@type": "Answer",
                    text: item.text, // Answer from the CMS item
                },
            })),
        };
    };

    return (
        <div className={cn(S.wrapper, paddingClass, positionClassName, backgroundColorClass)}>
            <DA_Container noVerticalPadding>
                <div className={S.inner}>
                    {/** Text */}
                    <div className={S.textWrapper}>
                        {/** Tagline */}
                        {props.tagline ? <span className={S.tagline}>{props.tagline}</span> : null}

                        {/** Title */}
                        {props.headline ? (
                            <DA_Title h2 tag={DA_TitleTag.H2} noMargin fullWidth>
                                {props.headline}
                            </DA_Title>
                        ) : null}

                        {/** Text */}
                        {props.text ? <DA_Text html={props.text} noPadding /> : null}

                        {/** Buttons */}
                        <Buttons buttons={props.buttons} theme={alternativeButtonTheme} />

                        {/** Image */}
                        {props.image ? (
                            <ImageViewer
                                className={S.image}
                                src={props.image.url}
                                aspectRatioX={props.image.width}
                                aspectRatioY={props.image.height}
                            />
                        ) : null}
                    </div>

                    {/** Accordions */}
                    <div className={S.items}>
                        {props.items?.map((item, index) => (
                            <DA_Accordion
                                key={index}
                                title={item.headline}
                                showToggleIcons
                                className={S.accordion}
                            >
                                <DA_Text html={item.text} />
                            </DA_Accordion>
                        ))}
                    </div>
                </div>
            </DA_Container>

            {/* Inject FAQ schema markup for all accordions */}

            {isFaqContent ? (
                <Helmet>
                    <script type="application/ld+json">
                        {JSON.stringify(generateFAQSchema())}
                    </script>
                </Helmet>
            ) : null}
        </div>
    );
};

export default TextAccordions;
